import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../../components/ui/Badge';
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { deletePmsDocument } from '../../../services/mutations';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import type { PmsDocument } from './useData';

type BoardTab = 'valid' | 'expired' | 'all';
type LookupRow = { id: number; code?: string | null; name?: string | null };

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'valid',
    label: 'Valid',
    icon: <CheckCircleIcon className="h-4 w-4" />,
    emptyTitle: 'No valid documents',
    emptyHint: 'Documents that are current (not expired) appear here.',
  },
  {
    id: 'expired',
    label: 'Expired',
    icon: <ClockIcon className="h-4 w-4" />,
    emptyTitle: 'No expired documents',
    emptyHint: 'Documents past their expiry date appear here.',
  },
  {
    id: 'all',
    label: 'All',
    icon: <DocumentTextIcon className="h-4 w-4" />,
    emptyTitle: 'No documents yet',
    emptyHint: 'Add manuals, certificates, and other PMS reference documents.',
  },
];

function listFrom<T>(rows: T[] | { data?: T[] } | undefined): T[] {
  return Array.isArray(rows) ? rows : rows?.data ?? [];
}

function vesselLabel(row: PmsDocument) {
  const v = row.vessel as { vesselName?: string; name?: string } | null | undefined;
  if (!v && row.vesselId == null) return 'Shared';
  return v?.vesselName ?? v?.name ?? (row.vesselId != null ? `#${row.vesselId}` : 'Shared');
}

function typeLabel(row: PmsDocument) {
  const t = row.documentType as { name?: string; code?: string } | null | undefined;
  return t?.name ?? t?.code ?? (row.documentTypeId != null ? `#${row.documentTypeId}` : '');
}

function expiryYmd(row: PmsDocument): string | null {
  if (row.expiryDate == null || row.expiryDate === '') return null;
  return String(row.expiryDate).slice(0, 10);
}

function isExpired(row: PmsDocument) {
  const exp = expiryYmd(row);
  if (!exp) return false;
  return exp < new Date().toISOString().slice(0, 10);
}

function formatDate(value?: unknown) {
  if (value == null || value === '') return '';
  const raw = String(value);
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00.000Z` : raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: raw.length === 10 ? 'UTC' : undefined,
  });
}

function sortValue(row: PmsDocument, key: string): string | number {
  switch (key) {
    case 'title':
      return String(row.title ?? '').toLowerCase();
    case 'documentNumber':
      return String(row.documentNumber ?? '').toLowerCase();
    case 'vessel':
      return vesselLabel(row).toLowerCase();
    case 'type':
      return typeLabel(row).toLowerCase();
    case 'revision':
      return String(row.revision ?? '').toLowerCase();
    case 'expiryDate':
      return expiryYmd(row) ?? '9999-99-99';
    case 'createdAt':
      return row.createdAt ? String(row.createdAt) : '';
    default:
      return '';
  }
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const confirm = useConfirm();

  const [tab, setTab] = useState<BoardTab>('valid');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });
  const { data: typesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-document-types?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(() => {
    const list = listFrom(vesselsRaw).filter((v) => v.isActive !== false);
    return list.map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  const typeOptions = useMemo(() => {
    const list = listFrom(typesRaw);
    return list.map((t) => ({ value: String(t.id), label: t.name ?? t.code ?? `#${t.id}` }));
  }, [typesRaw]);

  const listUrl = useMemo(() => {
    const params = new URLSearchParams({ page: '1', limit: '500' });
    if (search.trim()) params.set('searchParams', search.trim());
    return `/pms-documents?${params.toString()}`;
  }, [search]);

  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher, {
    revalidateOnFocus: true,
  });

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'title', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'title' || key === 'documentNumber' || key === 'vessel' || key === 'type' || key === 'revision'
          ? 'asc'
          : key === 'expiryDate'
            ? 'asc'
            : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [search, vesselId, documentTypeId, tab, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const baseRows = useMemo(() => {
    const normalized = normalizePaginatedListResponse<PmsDocument>(data, 1, 500);
    return normalized.data.filter((row) => {
      if (vesselId && String(row.vesselId) !== vesselId) return false;
      if (documentTypeId && String(row.documentTypeId) !== documentTypeId) return false;
      return true;
    });
  }, [data, vesselId, documentTypeId]);

  const tabRows = useMemo(() => {
    if (tab === 'all') return baseRows;
    return baseRows.filter((row) => (tab === 'expired' ? isExpired(row) : !isExpired(row)));
  }, [baseRows, tab]);

  const validCount = useMemo(() => baseRows.filter((row) => !isExpired(row)).length, [baseRows]);
  const expiredCount = useMemo(() => baseRows.filter((row) => isExpired(row)).length, [baseRows]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...tabRows].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -dir : dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [tabRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(search.trim() || vesselId || documentTypeId);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setDocumentTypeId('');
  };

  const switchTab = (next: BoardTab) => {
    if (next === tab) return;
    setTab(next);
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === tab) ?? viewTabs[0], [tab]);

  const tabCounts: Record<BoardTab, number | undefined> = {
    valid: validCount,
    expired: expiredCount,
    all: baseRows.length,
  };

  const handleDelete = async (e: React.MouseEvent, row: PmsDocument) => {
    e.stopPropagation();
    const label = row.title != null ? String(row.title) : `Document #${row.id}`;
    const ok = await confirm({
      title: 'Delete document',
      message: `Delete “${label}”?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deletePmsDocument(row.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || (err instanceof Error ? err.message : 'Failed to delete document'));
    }
  };

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
        count: tabCounts[id],
      }))}
      activeTab={tab}
      onTabChange={(value) => switchTab(value as BoardTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search documents…',
        title: 'Searches title and document number',
      }}
      actions={
        <Link
          to="/pms/pms-documents/new"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New document
        </Link>
      }
      filters={
        <>
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[150px] flex-1 basis-[150px]">
              <Select
                value={vesselId}
                options={vesselOptions}
                placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
                showClearButton
                searchPlaceholder="Search vessels…"
                onChange={(e) => setVesselId(String(e.target.value))}
              />
            </div>
            <div className="min-w-[150px] flex-1 basis-[150px]">
              <Select
                value={documentTypeId}
                options={typeOptions}
                placeholder="Document type"
                showClearButton
                onChange={(e) => setDocumentTypeId(String(e.target.value))}
              />
            </div>
          </div>
        </>
      }
      resultSummary={
        !isLoading ? (
          <p className={cn('text-xs', colors.textMuted)}>
            {total === 1 ? '1 document' : `${total} documents`}
            {hasFilters ? ' matching filters' : ''}
            {search.trim() && !hasFilters ? ` matching “${search.trim()}”` : ''}
          </p>
        ) : (
          <span />
        )
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading documents…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon:
          tab === 'valid' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching documents' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to="/pms/pms-documents/new"
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New document
          </Link>
        ),
      }}
      banner={
        error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load documents.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className="w-[5.5rem] px-3 py-3 font-medium" />
            <GeneralListSortableTh sortKey="title" className="w-[28%]">
              Document
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="hidden w-[14%] md:table-cell">
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="type" className="hidden w-[14%] sm:table-cell">
              Type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="revision" className="hidden w-[10%] lg:table-cell">
              Rev
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="expiryDate" className="w-[14%]">
              Expiry
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="createdAt" className="hidden w-[12%] lg:table-cell">
              Created
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const title = row.title != null ? String(row.title) : `Doc #${row.id}`;
            const expired = isExpired(row);
            const exp = expiryYmd(row);
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/pms/pms-documents/${row.id}`, { state: returnState })}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td className="px-3 py-3.5">
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-documents/${row.id}`}
                      state={returnState}
                      title="View"
                      aria-label="View document"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-documents/${row.id}/edit`}
                      state={returnState}
                      title="Edit"
                      aria-label="Edit document"
                      className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete document"
                      onClick={(e) => handleDelete(e, row)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
                <td className="max-w-0 px-3 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>{title}</p>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {row.documentNumber != null ? String(row.documentNumber) : ''}
                    </p>
                    <p className={cn('mt-1 truncate text-xs md:hidden', colors.textMuted)}>
                      {[vesselLabel(row), typeLabel(row)].filter((v) => Boolean(v)).join(' · ') || ''}
                    </p>
                  </div>
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 md:table-cell', colors.textMuted)}
                  title={vesselLabel(row)}
                >
                  {vesselLabel(row)}
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 sm:table-cell', colors.textMuted)}
                  title={typeLabel(row)}
                >
                  {typeLabel(row)}
                </td>
                <td className={cn('hidden whitespace-nowrap px-3 py-3.5 lg:table-cell', colors.textMuted)}>
                  {row.revision != null ? String(row.revision) : ''}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  {!exp ? (
                    <span className={colors.textMuted}></span>
                  ) : (
                    <Badge variant={expired ? 'danger' : 'success'} className="whitespace-nowrap">
                      {formatDate(row.expiryDate)}
                    </Badge>
                  )}
                </td>
                <td className={cn('hidden whitespace-nowrap px-3 py-3.5 lg:table-cell', colors.textMuted)}>
                  {formatDate(row.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
